//Dependencies
import React, {
  useState,
  useRef,
  useId,
  forwardRef,
  useImperativeHandle,
  Ref,
  useCallback,
  useLayoutEffect,
} from 'react';
import { ca } from 'date-fns/locale';
import { format, add, parse, isValid, isAfter, isBefore } from 'date-fns';
import { useClickAway } from 'react-use';

// Styled Components
import {
  Container,
  Calendar,
  InputContainer,
  InputDate,
  Label,
  InputLabel,
} from './styles';

// DayPicker Styles
import { DayPickerGlobalStyles } from './DayPicker';
// Lógica por defecto de construcción de rango (para el rango incompleto).
import { addToRange } from './DayPicker';

// Types
import {
  CalendarProps,
  ImperativeRefCalendarInput,
  onSelectCalendar,
} from './types';
import { DateRange } from './DayPicker';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

const InputCalendar = (
  { label, name, dateRange, onChangeInput, disabled, ...props }: CalendarProps,
  innerRef: Ref<ImperativeRefCalendarInput> | undefined
): JSX.Element => {
  const [range, setRange] = useState<DateRange | undefined>(dateRange);

  const [displayCalendar, setDisplayCalendar] = useState(false);

  const [manualFromInput, setManualFromInput] = useState('');
  const [isEditingFrom, setIsEditingFrom] = useState(false);
  const [manualToInput, setManualToInput] = useState('');
  const [isEditingTo, setIsEditingTo] = useState(false);

  // Extremo "activo": qué input pulsó el usuario para abrir el calendario.
  // Con un rango YA completo, un clic en el calendario edita SOLO este extremo
  // (input izquierdo → mueve `from`; derecho → mueve `to`). Por defecto 'from'.
  const [activeEdge, setActiveEdge] = useState<'from' | 'to'>('from');

  const today = new Date();
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    dateRange?.from ?? add(today, { months: -1 })
  );

  const initDay = range?.from ? format(range.from, 'dd/MM/yyyy') : '';
  const endDay = range?.to ? format(range.to, 'dd/MM/yyyy') : '';

  const calendarRef = useRef(null);
  const childRef = useRef<HTMLDivElement>(null);
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const nextFromCursorRef = useRef<number | null>(null);
  const nextToCursorRef = useRef<number | null>(null);

  useClickAway(calendarRef, () => setDisplayCalendar(false));

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => setRange(undefined),
  }));

  const onClickFromInput = useCallback(() => {
    setActiveEdge('from');
    setDisplayCalendar(true);
  }, []);

  const onClickToInput = useCallback(() => {
    setActiveEdge('to');
    setDisplayCalendar(true);
  }, []);

  const isLandscapeTablet = useIsLandscapeTablet();

  const onSelectCalendar = useCallback<onSelectCalendar>(
    (value, day) => {
      // Sin día (caso defensivo) → comportamiento por defecto del picker.
      if (!day) {
        setRange(value);
        if (onChangeInput && value?.from && value?.to) {
          onChangeInput(value);
          setDisplayCalendar(false);
        }
        return;
      }

      const from = range?.from;
      const to = range?.to;

      // Rango INCOMPLETO (0 o 1 extremo): construir con la lógica por defecto
      // (primer clic = from, segundo = to). Este flujo no es el que molesta.
      // Rango COMPLETO: editar SOLO el extremo activo (el input que el usuario
      // pulsó), con clamp para no invertir from/to. Esto arregla que, con el
      // input izquierdo activo, un clic DENTRO del rango encoja por la izquierda
      // (movía siempre el `to` = encogía por la derecha, ignorando el input).
      let next: DateRange | undefined;
      if (!from || !to) {
        next = addToRange(day, range);
      } else if (activeEdge === 'from') {
        next = isAfter(day, to) ? { from, to: day } : { from: day, to };
      } else {
        next = isBefore(day, from) ? { from: day, to } : { from, to: day };
      }

      setRange(next);
      if (onChangeInput && next?.from && next?.to) {
        onChangeInput(next);
        setDisplayCalendar(false);
      }
    },
    // `onChangeInput` DEBE ir en las deps. Con deps `[]` este callback
    // capturaba el `onChangeInput` del PRIMER render. En tablas con filtros
    // multi-select, ese handler reconstruye el cursor leyendo states/userIds
    // del closure: con el closure rancio leía `undefined` y al elegir un rango
    // en el calendario borraba la selección del multi-select. Mismo patrón que
    // applyFromDate/applyToDate, que ya incluían onChangeInput en sus deps.
    // `range`/`activeEdge` también van en deps: el cálculo focus-aware los lee.
    [onChangeInput, range, activeEdge],
  );

  const onCloseCalendar = useCallback(() => {
    setDisplayCalendar(false);
  }, []);

  const id = useId();

  const formatDateInput = (raw: string): string => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    let formatted = digitsOnly;
    if (digitsOnly.length > 2) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    if (digitsOnly.length > 4) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
    }
    return formatted;
  };

  const applyFromDate = useCallback(
    (raw: string) => {
      const parsed = parse(raw, 'dd/MM/yyyy', new Date());
      if (isValid(parsed) && parsed.getFullYear() >= 1900 && parsed <= today) {
        setIsEditingFrom(false);
        setManualFromInput('');
        const newRange = { from: parsed, to: range?.to };
        setRange(newRange);
        setCalendarMonth(parsed);
        if (range?.to && onChangeInput) onChangeInput(newRange);
      }
    },
    [range, onChangeInput, today]
  );

  const applyToDate = useCallback(
    (raw: string) => {
      const parsed = parse(raw, 'dd/MM/yyyy', new Date());
      if (isValid(parsed) && parsed.getFullYear() >= 1900 && parsed <= today) {
        if (!range?.from || parsed >= range.from) {
          setIsEditingTo(false);
          setManualToInput('');
          const newRange: DateRange = { from: range?.from, to: parsed };
          setRange(newRange);
          setCalendarMonth(parsed);
          if (range?.from && onChangeInput) {
            onChangeInput({ from: range.from, to: parsed });
            setDisplayCalendar(false);
          }
        }
      }
    },
    [range, onChangeInput, today]
  );

  const handleFromInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const cursorPos = e.target.selectionStart ?? rawValue.length;
      nextFromCursorRef.current = rawValue
        .slice(0, cursorPos)
        .replace(/[^\d]/g, '').length;

      const formatted = formatDateInput(rawValue);
      setManualFromInput(formatted);
      setIsEditingFrom(true);
      if (formatted.length === 10) applyFromDate(formatted);
    },
    [applyFromDate]
  );

  const handleToInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const cursorPos = e.target.selectionStart ?? rawValue.length;
      nextToCursorRef.current = rawValue
        .slice(0, cursorPos)
        .replace(/[^\d]/g, '').length;

      const formatted = formatDateInput(rawValue);
      setManualToInput(formatted);
      setIsEditingTo(true);
      if (formatted.length === 10) applyToDate(formatted);
    },
    [applyToDate]
  );

  const handleFromInputBlur = useCallback(() => {
    if (isEditingFrom && manualFromInput.length === 10)
      applyFromDate(manualFromInput);
    setIsEditingFrom(false);
    setManualFromInput('');
  }, [isEditingFrom, manualFromInput, applyFromDate]);

  const handleToInputBlur = useCallback(() => {
    if (isEditingTo && manualToInput.length === 10) applyToDate(manualToInput);
    setIsEditingTo(false);
    setManualToInput('');
  }, [isEditingTo, manualToInput, applyToDate]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  useLayoutEffect(() => {
    if (nextFromCursorRef.current === null || !fromInputRef.current) return;
    const input = fromInputRef.current;
    const val = input.value;
    const target = nextFromCursorRef.current;
    nextFromCursorRef.current = null;
    if (target === 0) {
      input.setSelectionRange(0, 0);
      return;
    }
    let pos = val.length;
    let count = 0;
    for (let i = 0; i < val.length; i++) {
      if (/\d/.test(val[i])) {
        count++;
        if (count === target) {
          pos = i + 1;
          while (pos < val.length && !/\d/.test(val[pos])) pos++;
          break;
        }
      }
    }
    input.setSelectionRange(pos, pos);
  }, [manualFromInput]);

  useLayoutEffect(() => {
    if (nextToCursorRef.current === null || !toInputRef.current) return;
    const input = toInputRef.current;
    const val = input.value;
    const target = nextToCursorRef.current;
    nextToCursorRef.current = null;
    if (target === 0) {
      input.setSelectionRange(0, 0);
      return;
    }
    let pos = val.length;
    let count = 0;
    for (let i = 0; i < val.length; i++) {
      if (/\d/.test(val[i])) {
        count++;
        if (count === target) {
          pos = i + 1;
          while (pos < val.length && !/\d/.test(val[pos])) pos++;
          break;
        }
      }
    }
    input.setSelectionRange(pos, pos);
  }, [manualToInput]);

  return (
    <Container {...props} ref={calendarRef}>
      <DayPickerGlobalStyles />
      <InputContainer className="input-container">
        <InputLabel>
          <Label
            size={isLandscapeTablet ? 'XS' : 'S'}
            component="label"
            htmlFor={`${id}-${name}`}
          >
            {label}
          </Label>
          <InputDate
            ref={fromInputRef}
            id={`${id}-${name}`}
            name={name}
            type="text"
            autoComplete="off"
            inputMode={isLandscapeTablet ? undefined : 'numeric'}

            readOnly={isLandscapeTablet}
            placeholder="dd/mm/aaaa"
            value={isEditingFrom ? manualFromInput : initDay}
            onClick={onClickFromInput}
            onChange={isLandscapeTablet ? undefined : handleFromInputChange}
            onBlur={isLandscapeTablet ? undefined : handleFromInputBlur}
            onKeyDown={isLandscapeTablet ? undefined : handleKeyDown}
            disabled={disabled ?? false}
          />
        </InputLabel>
        <InputDate
          ref={toInputRef}
          type="text"
          autoComplete="off"
          inputMode={isLandscapeTablet ? undefined : 'numeric'}
          pattern={isLandscapeTablet ? undefined : '[0-9]*'}
          readOnly={isLandscapeTablet}
          placeholder="dd/mm/aaaa"
          value={isEditingTo ? manualToInput : endDay}
          onClick={onClickToInput}
          onChange={isLandscapeTablet ? undefined : handleToInputChange}
          onBlur={isLandscapeTablet ? undefined : handleToInputBlur}
          onKeyDown={isLandscapeTablet ? undefined : handleKeyDown}
          disabled={disabled ?? false}
        />
      </InputContainer>
      {displayCalendar && (
        <Calendar
          ref={childRef}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          toDate={today}
          mode="range"
          locale={ca}
          hideHead={false}
          selected={range}
          onSelect={onSelectCalendar}
          numberOfMonths={2}
          onClose={onCloseCalendar}
        />
      )}
    </Container>
  );
};

export default forwardRef(InputCalendar);
